import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { Link } from "@tanstack/react-router";
import { useEffect, useState, type FormEvent } from "react";
import { downloadClientsApi } from "@/api/downloadClients";
import type {
  ClientKind,
  CreatableClientKind,
  CreateDownloadClient,
  DownloadClientRow,
  EmbeddedTestResult,
  ProtocolKind,
} from "@/api/types";
import { Button } from "@/components/ui/Button";
import { EmptyState } from "@/components/ui/EmptyState";
import { Field, NumberInput, Select, TextInput } from "@/components/ui/Field";
import { Modal } from "@/components/ui/Modal";
import { PageHeader } from "@/components/ui/PageHeader";
import { useToast } from "@/components/ui/Toast";

type EditingState =
  | { mode: "closed" }
  | { mode: "create" }
  | { mode: "edit"; row: DownloadClientRow };

/// State for the per-server probe modal that renders when the user
/// clicks "Test" on the built-in NZB row. `null` = not open.
type EmbeddedProbeView = {
  rowName: string;
  result: EmbeddedTestResult;
} | null;

export function DownloadClients() {
  const qc = useQueryClient();
  const { toast } = useToast();
  const [editing, setEditing] = useState<EditingState>({ mode: "closed" });
  const [testingId, setTestingId] = useState<number | null>(null);
  const [probeView, setProbeView] = useState<EmbeddedProbeView>(null);

  const { data: clients, isLoading } = useQuery({
    queryKey: ["download-clients"],
    queryFn: downloadClientsApi.list,
  });

  const removeMut = useMutation({
    mutationFn: (id: number) => downloadClientsApi.remove(id),
    onSuccess: () => {
      toast("Download client removed", "success");
      qc.invalidateQueries({ queryKey: ["download-clients"] });
    },
    onError: (e) => toast(e instanceof Error ? e.message : "Remove failed", "error"),
  });

  // PATCH-only update for the embedded row's enabled toggle, lives in
  // the table so the user can flip it without opening any modal.
  const toggleEnabledMut = useMutation({
    mutationFn: ({ id, enabled }: { id: number; enabled: boolean }) =>
      downloadClientsApi.update(id, { enabled }),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["download-clients"] });
    },
    onError: (e) => toast(e instanceof Error ? e.message : "Update failed", "error"),
  });

  const test = async (row: DownloadClientRow) => {
    setTestingId(row.id);
    try {
      const res = await downloadClientsApi.test(row);
      if (res.kind === "embedded") {
        // Per-server breakdown surfaces in a modal so the user can see
        // exactly which server failed. Fall back to a toast if the
        // result is empty (no servers configured / engine off).
        if (res.result.servers.length === 0) {
          toast(
            res.result.message ?? "No news servers configured",
            res.result.ok ? "success" : "error",
          );
        } else {
          setProbeView({ rowName: row.name, result: res.result });
        }
      } else {
        toast(
          res.result.ok
            ? `${row.name} is reachable`
            : `${row.name} failed: ${res.result.message ?? "unknown"}`,
          res.result.ok ? "success" : "error",
        );
      }
      // Health column reflects the persisted result.
      qc.invalidateQueries({ queryKey: ["download-clients"] });
    } catch (e) {
      toast(e instanceof Error ? e.message : "Test failed", "error");
    } finally {
      setTestingId(null);
    }
  };

  return (
    <div className="space-y-6">
      <PageHeader
        title="Download Clients"
        description="qBittorrent / SABnzbd / NZBGet endpoints (and the built-in NZB downloader) that receive grabbed releases."
        actions={<Button onClick={() => setEditing({ mode: "create" })}>Add client</Button>}
      />

      {isLoading ? (
        <p className="text-sm text-foreground/60">Loading…</p>
      ) : !clients || clients.length === 0 ? (
        <EmptyState
          title="No download clients configured"
          description="Add at least one to receive grabbed releases."
          action={<Button onClick={() => setEditing({ mode: "create" })}>Add client</Button>}
        />
      ) : (
        <div className="overflow-x-auto rounded border border-line">
          <table className="w-full text-sm">
            <thead className="bg-surface-2/50 text-left text-xs uppercase text-foreground/60">
              <tr>
                <th className="px-4 py-2 font-medium">Name</th>
                <th className="px-4 py-2 font-medium">Kind</th>
                <th className="px-4 py-2 font-medium">Protocol</th>
                <th className="px-4 py-2 font-medium">Endpoint</th>
                <th className="px-4 py-2 font-medium">Health</th>
                <th className="px-4 py-2"></th>
              </tr>
            </thead>
            <tbody>
              {clients.map((row) => {
                const isEmbedded = row.kind === "embedded";
                return (
                  <tr key={row.id} className="border-t border-line">
                    <td className="px-4 py-2 font-medium">
                      {row.name}
                      {!row.enabled && (
                        <span className="ml-2 rounded bg-muted px-1.5 py-0.5 text-[10px] uppercase text-foreground/60">
                          disabled
                        </span>
                      )}
                    </td>
                    <td className="px-4 py-2 capitalize text-foreground/70">
                      {isEmbedded ? (
                        <span className="rounded bg-blue-500/15 px-1.5 py-0.5 text-[10px] font-medium uppercase text-blue-300">
                          built-in
                        </span>
                      ) : (
                        row.kind
                      )}
                    </td>
                    <td className="px-4 py-2 capitalize text-foreground/70">{row.protocol}</td>
                    <td className="px-4 py-2 font-mono text-xs text-foreground/60">
                      {isEmbedded ? (
                        <span className="text-foreground/40">(local)</span>
                      ) : (
                        <>
                          {row.use_ssl ? "https" : "http"}://{row.host}:{row.port}
                        </>
                      )}
                    </td>
                    <td
                      className="px-4 py-2 text-xs"
                      title={
                        row.last_health_check_at
                          ? `Last checked ${new Date(row.last_health_check_at).toLocaleString()}`
                          : undefined
                      }
                    >
                      {row.healthy === null ? (
                        <span className="text-foreground/40">untested</span>
                      ) : row.healthy ? (
                        <span className="text-green-400">healthy</span>
                      ) : (
                        <span className="text-red-400">unhealthy</span>
                      )}
                    </td>
                    <td className="space-x-2 px-4 py-2 text-right">
                      <Button
                        variant="secondary"
                        size="sm"
                        loading={testingId === row.id}
                        onClick={() => test(row)}
                      >
                        Test
                      </Button>
                      {isEmbedded ? (
                        <>
                          <Button
                            variant="secondary"
                            size="sm"
                            onClick={() =>
                              toggleEnabledMut.mutate({ id: row.id, enabled: !row.enabled })
                            }
                          >
                            {row.enabled ? "Disable" : "Enable"}
                          </Button>
                          <Link
                            to="/settings/nzb"
                            className="inline-flex items-center rounded border border-line bg-transparent px-3 py-1 text-xs font-medium text-foreground/80 hover:bg-surface-2/50"
                          >
                            Configure servers
                          </Link>
                        </>
                      ) : (
                        <>
                          <Button
                            variant="secondary"
                            size="sm"
                            onClick={() => setEditing({ mode: "edit", row })}
                          >
                            Edit
                          </Button>
                          <Button
                            variant="danger"
                            size="sm"
                            onClick={() => {
                              if (confirm(`Remove client "${row.name}"?`))
                                removeMut.mutate(row.id);
                            }}
                          >
                            Remove
                          </Button>
                        </>
                      )}
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      )}

      <ClientFormModal editing={editing} onClose={() => setEditing({ mode: "closed" })} />

      <EmbeddedProbeModal view={probeView} onClose={() => setProbeView(null)} />
    </div>
  );
}

interface FormState {
  name: string;
  kind: CreatableClientKind;
  protocol: ProtocolKind;
  enabled: boolean;
  priority: number;
  host: string;
  port: number;
  use_ssl: boolean;
  username: string;
  password: string;
  api_key: string;
  category: string;
}

const defaultForKind = (kind: CreatableClientKind): Partial<FormState> => {
  switch (kind) {
    case "qbittorrent":
      return { protocol: "torrent", port: 8080, username: "admin" };
    case "sabnzbd":
      return { protocol: "usenet", port: 8080, username: "" };
    case "nzbget":
      return { protocol: "usenet", port: 6789, username: "nzbget" };
  }
};

const blankForm = (): FormState => ({
  name: "",
  kind: "nzbget",
  protocol: "usenet",
  enabled: true,
  priority: 25,
  host: "127.0.0.1",
  port: 6789,
  use_ssl: false,
  username: "nzbget",
  password: "",
  api_key: "",
  category: "",
});

const formFromRow = (row: DownloadClientRow): FormState => ({
  name: row.name,
  // The Edit modal is never shown for embedded rows, so we can safely
  // narrow here. If a future caller paths around the guard the cast
  // surfaces as a runtime error in the kind dropdown.
  kind: row.kind as CreatableClientKind,
  protocol: row.protocol,
  enabled: row.enabled,
  priority: row.priority,
  host: row.host,
  port: row.port,
  use_ssl: row.use_ssl,
  username: row.username ?? "",
  // Secrets aren't returned by the API on GET — leave blank, treat as
  // "keep existing" on save. The placeholder makes the contract clear.
  password: "",
  api_key: "",
  category: row.category ?? "",
});

const toCreate = (f: FormState): CreateDownloadClient => ({
  name: f.name,
  kind: f.kind as ClientKind,
  protocol: f.protocol,
  enabled: f.enabled,
  priority: f.priority,
  host: f.host,
  port: f.port,
  use_ssl: f.use_ssl,
  username: f.username.trim() ? f.username : null,
  password: f.password.trim() ? f.password : null,
  api_key: f.api_key.trim() ? f.api_key : null,
  category: f.category.trim() ? f.category : null,
});

function ClientFormModal({
  editing,
  onClose,
}: {
  editing: EditingState;
  onClose: () => void;
}) {
  const qc = useQueryClient();
  const { toast } = useToast();
  const [form, setForm] = useState<FormState>(blankForm());
  const [submitting, setSubmitting] = useState(false);
  const [testing, setTesting] = useState(false);

  const open = editing.mode !== "closed";
  const isEdit = editing.mode === "edit";
  const editingRow = editing.mode === "edit" ? editing.row : null;

  // Re-seed the form whenever the modal transitions from closed → open or
  // we switch between create and edit (or between two different rows).
  useEffect(() => {
    if (editing.mode === "create") setForm(blankForm());
    if (editing.mode === "edit") setForm(formFromRow(editing.row));
  }, [editing]);

  const onKindChange = (kind: CreatableClientKind) => {
    setForm((f) => ({ ...f, kind, ...defaultForKind(kind) }));
  };

  const submit = async (e: FormEvent) => {
    e.preventDefault();
    setSubmitting(true);
    try {
      if (isEdit && editingRow) {
        // PATCH: only send password / api_key if user actually typed something
        // new. Sending null would clear the stored value via COALESCE.
        await downloadClientsApi.update(editingRow.id, {
          name: form.name,
          enabled: form.enabled,
          priority: form.priority,
          host: form.host,
          port: form.port,
          use_ssl: form.use_ssl,
          username: form.username.trim() ? form.username : null,
          password: form.password.trim() ? form.password : undefined,
          api_key: form.api_key.trim() ? form.api_key : undefined,
          category: form.category.trim() ? form.category : null,
        });
        toast("Download client updated", "success");
      } else {
        await downloadClientsApi.create(toCreate(form));
        toast("Download client added", "success");
      }
      qc.invalidateQueries({ queryKey: ["download-clients"] });
      onClose();
    } catch (err) {
      toast(err instanceof Error ? err.message : "Save failed", "error");
    } finally {
      setSubmitting(false);
    }
  };

  const runTest = async () => {
    setTesting(true);
    try {
      const res = await downloadClientsApi.testConfig(toCreate(form));
      toast(
        res.ok ? "Connection succeeded" : `Connection failed: ${res.message ?? "unknown"}`,
        res.ok ? "success" : "error",
      );
    } catch (err) {
      toast(err instanceof Error ? err.message : "Test failed", "error");
    } finally {
      setTesting(false);
    }
  };

  // SAB uses api_key; qBit + NZBGet use user/pass.
  const usesApiKey = form.kind === "sabnzbd";
  const usesUserPass = form.kind === "qbittorrent" || form.kind === "nzbget";
  const secretPlaceholder = isEdit ? "leave blank to keep existing" : "";

  return (
    <Modal
      open={open}
      onClose={onClose}
      title={isEdit ? `Edit ${editingRow?.name ?? "client"}` : "Add download client"}
      width="lg"
    >
      <form onSubmit={submit} className="grid grid-cols-2 gap-4">
        <Field label="Name">
          <TextInput
            required
            value={form.name}
            onChange={(e) => setForm((f) => ({ ...f, name: e.target.value }))}
            placeholder="e.g. nzbget-prod"
          />
        </Field>
        <Field
          label="Kind"
          hint={isEdit ? "Cannot be changed after creation" : undefined}
        >
          <Select
            value={form.kind}
            disabled={isEdit}
            onChange={(e) => onKindChange(e.target.value as CreatableClientKind)}
          >
            <option value="qbittorrent">qBittorrent</option>
            <option value="sabnzbd">SABnzbd</option>
            <option value="nzbget">NZBGet</option>
          </Select>
        </Field>
        <Field label="Host">
          <TextInput
            required
            value={form.host}
            onChange={(e) => setForm((f) => ({ ...f, host: e.target.value }))}
            placeholder="127.0.0.1"
          />
        </Field>
        <Field label="Port">
          <NumberInput
            required
            value={form.port}
            onChange={(e) => setForm((f) => ({ ...f, port: Number(e.target.value) || 0 }))}
          />
        </Field>
        <Field
          label="Protocol"
          hint={isEdit ? "Cannot be changed after creation" : undefined}
        >
          <Select
            value={form.protocol}
            disabled={isEdit}
            onChange={(e) =>
              setForm((f) => ({ ...f, protocol: e.target.value as ProtocolKind }))
            }
          >
            <option value="usenet">Usenet</option>
            <option value="torrent">Torrent</option>
          </Select>
        </Field>
        <Field label="HTTPS">
          <Select
            value={form.use_ssl ? "1" : "0"}
            onChange={(e) => setForm((f) => ({ ...f, use_ssl: e.target.value === "1" }))}
          >
            <option value="0">No (http)</option>
            <option value="1">Yes (https)</option>
          </Select>
        </Field>
        {usesUserPass && (
          <>
            <Field label="Username">
              <TextInput
                value={form.username}
                onChange={(e) => setForm((f) => ({ ...f, username: e.target.value }))}
              />
            </Field>
            <Field label="Password" hint={isEdit ? secretPlaceholder : undefined}>
              <TextInput
                type="password"
                value={form.password}
                placeholder={secretPlaceholder}
                onChange={(e) => setForm((f) => ({ ...f, password: e.target.value }))}
              />
            </Field>
          </>
        )}
        {usesApiKey && (
          <Field label="API key" hint={isEdit ? secretPlaceholder : undefined}>
            <TextInput
              value={form.api_key}
              placeholder={secretPlaceholder}
              onChange={(e) => setForm((f) => ({ ...f, api_key: e.target.value }))}
            />
          </Field>
        )}
        <Field label="Category" hint="Optional. Sent on every download.">
          <TextInput
            value={form.category}
            onChange={(e) => setForm((f) => ({ ...f, category: e.target.value }))}
            placeholder="e.g. movies"
          />
        </Field>
        <Field label="Priority" hint="Lower number = tried first.">
          <NumberInput
            value={form.priority}
            onChange={(e) => setForm((f) => ({ ...f, priority: Number(e.target.value) || 0 }))}
          />
        </Field>
        <div className="col-span-2 flex items-center justify-between border-t border-line pt-3">
          <Button type="button" variant="secondary" onClick={runTest} loading={testing}>
            Test connection
          </Button>
          <div className="flex gap-2">
            <Button type="button" variant="ghost" onClick={onClose}>
              Cancel
            </Button>
            <Button type="submit" loading={submitting}>
              {isEdit ? "Save changes" : "Add"}
            </Button>
          </div>
        </div>
      </form>
    </Modal>
  );
}

/// Modal that surfaces the per-server probe breakdown for the embedded
/// NZB downloader. Each row is one news server, ok/fail + reason. The
/// modal opens automatically when the user clicks Test on the built-in
/// row and at least one server was checked.
function EmbeddedProbeModal({
  view,
  onClose,
}: {
  view: EmbeddedProbeView;
  onClose: () => void;
}) {
  const open = view !== null;
  const failureCount = view ? view.result.servers.filter((s) => !s.ok).length : 0;
  return (
    <Modal
      open={open}
      onClose={onClose}
      title={view ? `Test result — ${view.rowName}` : ""}
      width="lg"
    >
      {view && (
        <div className="space-y-4">
          <div
            className={
              view.result.ok
                ? "rounded border border-green-500/30 bg-green-500/10 px-3 py-2 text-sm text-green-300"
                : "rounded border border-red-500/30 bg-red-500/10 px-3 py-2 text-sm text-red-300"
            }
          >
            {view.result.ok
              ? `All ${view.result.servers.length} server(s) reachable.`
              : view.result.message ??
                `${failureCount} of ${view.result.servers.length} server(s) failed.`}
          </div>
          <div className="overflow-x-auto rounded border border-line">
            <table className="w-full text-sm">
              <thead className="bg-surface-2/50 text-left text-xs uppercase text-foreground/60">
                <tr>
                  <th className="px-3 py-2 font-medium">Server</th>
                  <th className="px-3 py-2 font-medium">Host</th>
                  <th className="px-3 py-2 font-medium">Status</th>
                  <th className="px-3 py-2 font-medium">Detail</th>
                </tr>
              </thead>
              <tbody>
                {view.result.servers.map((s) => (
                  <tr key={s.server_id} className="border-t border-line">
                    <td className="px-3 py-2 font-medium">{s.server_name}</td>
                    <td className="px-3 py-2 font-mono text-xs text-foreground/60">{s.host}</td>
                    <td className="px-3 py-2 text-xs">
                      {s.ok ? (
                        <span className="text-green-400">ok</span>
                      ) : (
                        <span className="text-red-400">failed</span>
                      )}
                    </td>
                    <td className="px-3 py-2 text-xs text-foreground/70">
                      {s.message ?? (s.ok ? "" : "—")}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
          <div className="flex justify-end">
            <Button variant="ghost" onClick={onClose}>
              Close
            </Button>
          </div>
        </div>
      )}
    </Modal>
  );
}
